<script>
import Layout from '@layouts/main'
import { cdmrMethods, authComputed } from '@state/helpers'

export default {
  metaInfo: {
    title: 'Edit CDM-R',
    meta: [{ name: 'description', content: 'Edit CDM-R' }],
  },
  components: { Layout },
  props: {
    fields: {
      type: Array,
      required: true,
    },
    initialCdmr: {
      type: Object,
      required: true,
    },
    initialChangesets: {
      type: Array,
      required: true,
    },
    approverWorkflow: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      active: this.initialChangesets.length - 1,
      newChangeset: {},
      changesets: this.initialChangesets,
      saveError: null,
      isWorking: false,
      finalComment: null,
      dialogReason: '',
      showDialog: false,
      dialogTitle: '',
      dialogDescription: '',
      cdmr: this.initialCdmr,
    }
  },
  computed: {
    ...authComputed,
    sortedFields() {
      var fields = this.fields
      return fields.sort(function(a, b) {
        return a.order - b.order
      })
    },
    sortedChangesets() {
      var changesets = this.changesets
      return changesets.sort(function(a, b) {
        return new Date(a.created).getTime() - new Date(b.created).getTime()
      })
    },
    mostRecentChangeset() {
      const sorted = this.sortedChangesets
      if (sorted) {
        return sorted[sorted.length - 1]
      } else {
        return null
      }
    },
    canMakeChanges() {
      return this.cdmr.status === 0
    },
    workflow() {
      var relevantWorkflow = this.approverWorkflow.filter(x => {
        for (var i = 0; i < x.role.userRoles.length; i++) {
          var ur = x.role.userRoles[i]
          if (ur.userId === this.initialCdmr.createdById) {
            return true
          }
        }

        return false
      })

      if (relevantWorkflow.length === 0) {
        return []
      }

      return relevantWorkflow[0].signoff.sort(function(a, b) {
        return a.order - b.order
      })
    },
    currentUserSignoff() {
      var currentUser = this.currentUser
      var highestSignoff = -1
      for (var a = 0; a < this.workflow.length; a++) {
        var approver = this.workflow[a]
        var found = false
        for (var r = 0; r < approver.role.userRoles.length; r++) {
          var ur = approver.role.userRoles[r]
          if (ur.userId === currentUser.id) {
            found = true
            break
          }
        }

        if (found && approver.order > highestSignoff) {
          highestSignoff = approver.order
        }
      }

      return highestSignoff
    },
    isCurrentSignoff() {
      return (
        (this.currentUser.id === this.cdmr.createdById &&
          this.changesets.length === 0) ||
        (this.currentUserSignoff !== -1 &&
          this.currentUserSignoff >= this.cdmr.currentWorkflow)
      )
    },
    isFinalSignoff() {
      return (
        this.workflow.length > 0 &&
        this.currentUserSignoff ===
          this.workflow[this.workflow.length - 1].order
      )
    },
    currentStatus() {
      var status = this.cdmr.status
      if (status === 0) {
        return 'Pending'
      }
      if (status === 1) {
        return 'Finalized'
      }
      if (status === 2) {
        return 'Exported'
      }
      if (status === 3) {
        return 'Denied'
      }

      return ''
    },
    cdmrType() {
      var type = this.cdmr.cdmrType
      if (type === 0) {
        return 'Add'
      }
      if (type === 1) {
        return 'Change'
      }
      if (type === 2) {
        return 'Purge'
      }

      return ''
    },
    changeTableClass() {
      if (this.cdmr.cdmrType === 2) {
        return `table table-striped ${this.$style.purgestyle}`
      }
      return `table table-striped`
    },
    changeTitle() {
      if (this.cdmr.cdmrType === 2) {
        return 'Purge'
      }
      return 'Your Changes'
    },
    approveButtonText() {
      if (this.cdmr.cdmrType === 2) {
        return 'Approve'
      }
      return 'Submit Changeset/Approve'
    },
  },
  watch: {
    dialogReason: {
      handler() {
        if (this.dialogReason === '') {
          this.finalComment = ''
          this.showDialog = false
        } else {
          this.showDialog = true
        }
      },
    },
  },
  created() {
    if (this.mostRecentChangeset) {
      this.newChangeset = JSON.parse(
        JSON.stringify(this.mostRecentChangeset.data)
      )
    } else {
      this.newChangeset = JSON.parse(JSON.stringify(this.cdmr.original))
    }
  },
  methods: {
    ...cdmrMethods,
    tryCreateChangeset() {
      this.isWorking = true
      this.saveError = null

      if (this.cdmr.cdmrType === 2) {
        return this.approveCdmr({
          cdmrId: this.cdmr.id,
          comment: this.finalComment,
        })
          .then(result => {
            this.isWorking = false
            this.cdmr = result.cdmr
            this.dialogReason = ''
          })
          .catch(error => {
            this.isWorking = false
            this.saveError = error

            this.$swal(
              'Error',
              typeof error === 'string'
                ? error
                : 'An error occurred approving this CDM-R.',
              'error'
            )
          })
      } else {
        var toSend = this.newChangeset
        return this.createChangeset({
          cdmrId: this.cdmr.id,
          changeset: toSend,
          comment: this.finalComment,
        })
          .then(result => {
            this.isWorking = false
            this.cdmr = result.cdmr
            this.changesets.push(result.changeset)
            this.newChangeset = JSON.parse(
              JSON.stringify(this.mostRecentChangeset.data)
            )
            this.active = this.changesets.length - 1
            this.dialogReason = ''
          })
          .catch(error => {
            this.isWorking = false
            this.saveError = error

            this.$swal(
              'Error',
              typeof error === 'string'
                ? error
                : 'An error occurred submitting your changes.',
              'error'
            )
          })
      }
    },
    tryApprove() {
      this.isWorking = true
      this.saveError = null

      return this.approveCdmr({
        cdmrId: this.cdmr.id,
        comment: this.finalComment,
      })
        .then(result => {
          this.isWorking = false
          this.cdmr = result.cdmr
          this.active = this.changesets.length - 1
          this.dialogReason = ''
        })
        .catch(error => {
          this.isWorking = false
          this.saveError = error

          this.$swal(
            'Error',
            typeof error === 'string'
              ? error
              : 'An error occurred submitting your approval.',
            'error'
          )
        })
    },
    finalizeConfirm() {
      this.dialogTitle = 'Are you sure you want to finalize?'
      this.dialogDescription =
        'This will close the CDM-R and you will not be able to make any further changes.'
      this.dialogReason = 'finalize'
    },
    createChangesetConfirm() {
      this.dialogTitle = 'Submitting Changeset'
      this.dialogDescription =
        'You can enter a comment below to provide additional information to the next approver.'
      this.dialogReason = 'changeset'
    },
    denyConfirm() {
      this.dialogTitle = 'Are you sure you want to deny?'
      this.dialogDescription =
        'This will close the CDM-R and you will not be able to make any further changes.'
      this.dialogReason = 'deny'
    },
    tryDeny() {
      this.isWorking = true
      this.saveError = null

      return this.denyCdmr({
        cdmrId: this.cdmr.id,
        finalComment: this.finalComment,
      })
        .then(result => {
          this.isWorking = false
          this.$router.push({ name: 'cdmrs' })
        })
        .catch(error => {
          this.isWorking = false
          this.saveError = error
        })
    },
    typeFromField(field) {
      if (field.type === 0) {
        return 'text'
      }
      if (field.type === 1) {
        return 'number'
      }
      if (field.type === 2) {
        return 'date'
      }
    },
    titleFromField(field) {
      return field.caption || field.name
    },
    acceptDialog() {
      switch (this.dialogReason) {
        case 'finalize':
          if (this.cdmr.cdmrType === 2) {
            this.tryApprove()
          } else {
            this.tryCreateChangeset()
          }
          break
        case 'deny':
          this.tryDeny()
          break
        case 'changeset':
          this.tryCreateChangeset()
      }
      this.dialogReason = ''
    },
  },
}
</script>

<template>
  <Layout>
    <!-- NEW -->
    <v-container fluid grid-list-md ma-0>
      <v-layout row justify-space-between>
        <h3>CDM-R Status: {{ currentStatus }}</h3>
        <p>Type: {{ cdmrType }}</p>
      </v-layout>

      <!-- Original card -->
      <v-layout>
        <v-flex md4>
          <v-card>
            <v-card-title :class="$style.originalTitle">
              <strong>Original</strong>
            </v-card-title>
            <v-card-text>
              <v-layout
                v-for="field in sortedFields"
                :key="field.id"
                row
                justify-space-between
                class="py-2 px-1"
              >
                <!-- Left -->
                <div v-if="field.caption">{{ field.caption }}</div>
                <div v-if="!field.caption">{{ field.name }}</div>

                <!-- Right -->
                <strong>{{ cdmr.original[field.name] }}</strong>
              </v-layout>
            </v-card-text>
          </v-card>
        </v-flex>

        <!-- Last change card -->
        <v-flex md4>
          <v-card>
            <v-card-title :class="$style.lastTitle">
              <strong>Last Change</strong>
            </v-card-title>
            <v-tabs v-model="active" slider-color="blue">
              <v-tab v-for="changeset in sortedChangesets" :key="changeset.id">
                <b v-if="changeset.id === mostRecentChangeset.id">
                  Last Change (by
                  {{
                    `${changeset.createdBy.firstName} ${
                      changeset.createdBy.lastName
                    }`
                  }})
                  <br />
                  {{ changeset.created | moment('MM-DD-YYYY h:mm:ss a') }}
                </b>
                <b v-else>
                  Change (by
                  {{
                    `${changeset.createdBy.firstName} ${
                      changeset.createdBy.lastName
                    }`
                  }})
                  <br />
                  {{ changeset.created | moment('MM-DD-YYYY h:mm:ss a') }}
                </b>
              </v-tab>
              <v-tab-item
                v-for="changeset in sortedChangesets"
                :key="changeset.id"
              >
                <br />
                <table class="table table-striped">
                  <tbody>
                    <tr v-for="field in sortedFields" :key="field.id">
                      <th scope="row">
                        <label v-if="field.caption">{{ field.caption }}</label>
                        <label v-if="!field.caption">{{ field.name }}</label>
                      </th>
                      <td>
                        <label>{{ changeset.data[field.name] }}</label>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </v-tab-item>
              <span v-if="!sortedChangesets.length"></span>
            </v-tabs>
          </v-card>
        </v-flex>

        <!-- Your change card -->
        <v-flex md4>
          <v-card>
            <v-card-title :class="$style.yourTitle">
              <strong>{{ changeTitle }}</strong>
            </v-card-title>
            <v-card-text>
              <div v-for="field in sortedFields" :key="field.id">
                <label v-if="field.caption" class="ma-0">{{
                  field.caption
                }}</label>
                <label v-else class="ma-0">{{ field.name }}</label>

                <label
                  v-if="
                    cdmr.cdmrType !== 0 &&
                      (!field.includeInCdmr || cdmr.cdmrType === 2)
                  "
                  class="ma-0"
                  >{{ newChangeset[field.name] }}</label
                >
                <v-text-field
                  v-else
                  v-model="newChangeset[field.name]"
                  color="cyan"
                  class="pa-0"
                  :name="field.name"
                  :type="typeFromField(field)"
                  :placeholder="titleFromField(field)"
                />
              </div>
            </v-card-text>
          </v-card>
        </v-flex>
      </v-layout>

      <!-- OLD -->
      <v-layout row>
        <v-card>
          <v-card-title>
            <b>
              CDM-R Status: {{ currentStatus }}
              <br />
              Type: {{ cdmrType }}
            </b>
          </v-card-title>
        </v-card>
      </v-layout>
      <v-layout row>
        <!-- Original CDM -->
        <v-card>
          <v-card-title>
            <b>Original</b>
          </v-card-title>
          <table class="table table-striped">
            <tbody>
              <tr v-for="field in sortedFields" :key="field.id">
                <th scope="row">
                  <label v-if="field.caption">{{ field.caption }}</label>
                  <label v-if="!field.caption">{{ field.name }}</label>
                </th>
                <td>
                  <label>{{ cdmr.original[field.name] }}</label>
                </td>
              </tr>
            </tbody>
          </table>
        </v-card>
        <!-- Most recent changeset -->
        <v-flex md4 ma-2>
          <v-tabs v-model="active" slider-color="blue">
            <v-tab v-for="changeset in sortedChangesets" :key="changeset.id">
              <b v-if="changeset.id === mostRecentChangeset.id">
                Last Change (by
                {{
                  `${changeset.createdBy.firstName} ${
                    changeset.createdBy.lastName
                  }`
                }})
                <br />
                {{ changeset.created | moment('MM-DD-YYYY h:mm:ss a') }}
              </b>
              <b v-else>
                Change (by
                {{
                  `${changeset.createdBy.firstName} ${
                    changeset.createdBy.lastName
                  }`
                }})
                <br />
                {{ changeset.created | moment('MM-DD-YYYY h:mm:ss a') }}
              </b>
            </v-tab>
            <v-tab-item
              v-for="changeset in sortedChangesets"
              :key="changeset.id"
            >
              <br />
              <table class="table table-striped">
                <tbody>
                  <tr v-for="field in sortedFields" :key="field.id">
                    <th scope="row">
                      <label v-if="field.caption">{{ field.caption }}</label>
                      <label v-if="!field.caption">{{ field.name }}</label>
                    </th>
                    <td>
                      <label>{{ changeset.data[field.name] }}</label>
                    </td>
                  </tr>
                </tbody>
              </table>
            </v-tab-item>
            <span v-if="!sortedChangesets.length"></span>
          </v-tabs>
        </v-flex>
        <!-- This user's changes -->
        <v-flex v-if="canMakeChanges" md4 ma-2>
          <v-card>
            <v-card-title>
              <b>{{ changeTitle }}</b>
            </v-card-title>
          </v-card>
          <br />
          <table :class="changeTableClass">
            <tbody>
              <tr v-for="field in sortedFields" :key="field.id">
                <th scope="row">
                  <label v-if="field.caption">{{ field.caption }}</label>
                  <label v-else>{{ field.name }}</label>
                </th>
                <td>
                  <label
                    v-if="
                      cdmr.cdmrType !== 0 &&
                        (!field.includeInCdmr || cdmr.cdmrType === 2)
                    "
                    >{{ newChangeset[field.name] }}</label
                  >
                  <v-text-field
                    v-else
                    v-model="newChangeset[field.name]"
                    :name="field.name"
                    :type="typeFromField(field)"
                    :placeholder="titleFromField(field)"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </v-flex>
      </v-layout>
      <v-layout
        v-if="cdmr.status === 0"
        align-start
        justify-space-between
        row
        fill-height
      >
        <v-flex xs12>
          <BaseButton
            v-if="isCurrentSignoff"
            :disabled="isWorking"
            type="button"
            :class="$style.savebutton"
            @click="denyConfirm"
          >
            <BaseIcon v-if="isWorking" name="sync" spin />
            <span v-else>Deny</span>
          </BaseButton>

          <BaseButton
            v-if="isFinalSignoff"
            :disabled="isWorking"
            type="button"
            :class="$style.savebutton"
            @click="finalizeConfirm"
          >
            <BaseIcon v-if="isWorking" name="sync" spin />
            <span v-else>Finalize</span>
          </BaseButton>
          <BaseButton
            v-if="isCurrentSignoff && !isFinalSignoff"
            :disabled="isWorking"
            type="button"
            :class="$style.savebutton"
            @click="createChangesetConfirm"
          >
            <BaseIcon v-if="isWorking" name="sync" spin />
            <span v-else>{{ approveButtonText }}</span>
          </BaseButton>
        </v-flex>
      </v-layout>

      <v-layout row justify-center>
        <v-dialog v-model="showDialog" persistent max-width="310">
          <v-card>
            <v-card-title class="headline">{{ dialogTitle }}</v-card-title>
            <v-card-text>{{ dialogDescription }}</v-card-text>
            <v-card-text>
              <v-container grid-list-md>
                <v-textarea
                  label="Comment (optional)"
                  :value="finalComment"
                ></v-textarea>
              </v-container>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="red darken-1" flat @click="dialogReason = ''"
                >No</v-btn
              >
              <v-btn color="green darken-1" flat @click="acceptDialog"
                >Yes</v-btn
              >
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-layout>
    </v-container>
  </Layout>
</template>

<style lang="scss" module>
@import '@design';

.savebutton {
  float: right;
  margin-right: 10px;
}

.purgestyle {
  background-color: red;
}

.originalTitle {
  background: rgba(#6c757d, 0.1);
  border-bottom: 2px solid var(--gray);
}

.lastTitle {
  background: rgba(#ffc107, 0.1);
  border-bottom: 2px solid var(--yellow);
}

.yourTitle {
  background: rgba(#17a2b8, 0.1);
  border-bottom: 2px solid var(--cyan);
}
</style>
